import { z } from 'zod';
import { email, globalRole, isoDateTime, paginationQuery, uuid } from './primitives.js';
// ------------------------------------------------------------------ progress ---
export const heartbeatRequest = z.object({
    positionSec: z.number().min(0),
    /** Wall-clock seconds of actual playback since the last beat. */
    deltaSec: z.number().min(0).max(120),
    durationSec: z.number().min(0).nullable().default(null),
});
export const heartbeatResponse = z.object({
    watchPct: z.number(),
    completed: z.boolean(),
    /** True on the beat that flipped completion, so the UI can celebrate once. */
    justCompleted: z.boolean(),
});
// --------------------------------------------------------------------- users ---
export const userRow = z.object({
    id: uuid,
    email,
    name: z.string(),
    globalRole,
    isActive: z.boolean(),
    lastLoginAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
    enrollmentCount: z.number().int(),
    assignedSessionCount: z.number().int(),
    managedCourseCount: z.number().int(),
    isGuideWriter: z.boolean(),
    hasLoggedIn: z.boolean(),
});
export const userListQuery = paginationQuery.extend({
    q: z.string().max(200).optional(),
    role: globalRole.optional(),
    sort: z
        .enum(['name', 'email', 'role', 'status', 'created_at', 'last_login_at', 'enrollment_count'])
        .default('name'),
    dir: z.enum(['asc', 'desc']).default('asc'),
});
export const updateUserRequest = z.object({
    globalRole: globalRole.optional(),
    isActive: z.boolean().optional(),
    name: z.string().max(200).optional(),
    isGuideWriter: z.boolean().optional(),
});
export const inviteUsersRequest = z.object({
    emails: z.array(email).min(1).max(1_000),
    globalRole: globalRole.default('member'),
    /** Optionally enroll every invited user into these courses immediately. */
    enrollInCourseIds: z.array(uuid).default([]),
    /** Email the people this adds. Off is for a quiet bulk import. */
    notify: z.boolean().default(true),
});
// ------------------------------------------------------------- notifications ---
export const notificationKind = z.enum([
    'invite',
    'course_enrolled',
    'session_assigned',
    'course_admin_assigned',
]);
/**
 * held: an enrolment email waiting for its course to be published.
 * skipped: deliberately not sent (email off, or no longer true when it was due).
 */
export const notificationStatus = z.enum(['held', 'pending', 'sent', 'failed', 'skipped']);
export const notificationRow = z.object({
    id: uuid,
    userId: uuid,
    kind: notificationKind,
    status: notificationStatus,
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    courseTitle: z.string().nullable(),
    sessionTitle: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: isoDateTime,
    sentAt: isoDateTime.nullable(),
});
/** What the invite, enrol and assign dialogs say about email. */
export const mailStatusResponse = z.object({
    mailEnabled: z.boolean(),
    /** The address emails come from, when they go anywhere. */
    from: z.string().nullable(),
});
export const userNotificationsResponse = z.object({
    /** False when the server has no mail sender; the console says so. */
    mailEnabled: z.boolean(),
    items: z.array(notificationRow),
});
// ----------------------------------------------------------------- audit log ---
export const auditEntry = z.object({
    id: z.number().int(),
    actorId: uuid.nullable(),
    actorEmail: z.string().nullable(),
    action: z.string(),
    resourceType: z.string(),
    resourceId: uuid.nullable(),
    courseId: uuid.nullable(),
    sessionId: uuid.nullable(),
    summary: z.string().nullable(),
    beforeState: z.unknown().nullable(),
    afterState: z.unknown().nullable(),
    ip: z.string().nullable(),
    createdAt: isoDateTime,
});
export const auditQuery = paginationQuery.extend({
    courseId: uuid.optional(),
    sessionId: uuid.optional(),
    actorId: uuid.optional(),
    action: z.string().max(100).optional(),
    resourceType: z.string().max(100).optional(),
    since: z.string().optional(),
});
// ----------------------------------------------------------------- analytics ---
export const courseAnalytics = z.object({
    courseId: uuid,
    courseTitle: z.string(),
    enrolled: z.number().int(),
    activeLast7d: z.number().int(),
    avgCompletionPct: z.number(),
    completedCourse: z.number().int(),
    funnel: z.array(z.object({
        sessionId: uuid,
        title: z.string(),
        orderIndex: z.number().int(),
        started: z.number().int(),
        completed: z.number().int(),
        avgWatchPct: z.number(),
    })),
    quizItemAnalysis: z.array(z.object({
        sessionId: uuid,
        sessionTitle: z.string(),
        questionId: uuid,
        promptMd: z.string(),
        attempts: z.number().int(),
        correctPct: z.number(),
    })),
    /** Counts only. There is no endpoint that can return chat content. */
    chatVolume: z.array(z.object({ day: z.string(), requests: z.number().int(), users: z.number().int() })),
});
// ---------------------------------------------------------------------- chat ---
/**
 * The AI chat is intentionally unwired in this build: the panel, the ephemeral
 * transport and the "not saved" contract are real, the model call is a stub.
 * `mode` tells the client which it is talking to so the UI can label itself
 * honestly rather than faking an answer that looks authoritative.
 */
export const chatScope = z.object({
    scopeType: z.enum(['session', 'capstone']),
    scopeId: uuid,
});
export const chatMessageRequest = chatScope.extend({
    message: z.string().min(1).max(4_000),
    /**
     * Prior turns are replayed by the client on every request. The server keeps
     * nothing: no table, no cache, no log line. This is the whole reason the
     * history travels in the request body.
     */
    history: z
        .array(z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string().max(8_000),
    }))
        .max(20)
        .default([]),
});
/** The learner session page's tabs, which a citation can open. */
export const chatSessionTab = z.enum([
    'video',
    'deck',
    'notes',
    'interactive',
    'quiz',
    'assignment',
    'resources',
]);
export const chatCitation = z.object({
    label: z.string(),
    /** Deep-link target inside the session video, when the chunk came from ASR. */
    startSec: z.number().nullable(),
    anchor: z.string().nullable(),
    /**
     * What the citation points at: a moment in the video, a material of this
     * session (opened through its tab), another session of the course, or a web
     * page. The stub's retrieval citations predate this and carry none of it.
     */
    kind: z.enum(['video', 'material', 'session', 'web']).optional(),
    /** The session tab that holds the material. */
    tab: chatSessionTab.nullable().optional(),
    /** An in-app path (another session) or an external URL (a link resource). */
    href: z.string().nullable().optional(),
    /** The number the reply's text uses for it: `[n](#cite-n)`. */
    index: z.number().int().optional(),
});
export const chatMessageResponse = z.object({
    mode: z.enum(['stub', 'live']),
    reply: z.string(),
    citations: z.array(chatCitation),
    /** Restated on every response; the UI surfaces it verbatim. */
    retentionNotice: z.string(),
});
/**
 * `POST /api/chat/stream` answers with server-sent events, one of these per
 * `data:` line. Text arrives as deltas; `done` carries the final reply with
 * its citation markers resolved, which replaces the streamed text.
 */
export const chatStreamEvent = z.discriminatedUnion('type', [
    z.object({ type: z.literal('reasoning'), delta: z.string() }),
    z.object({ type: z.literal('text'), delta: z.string() }),
    z.object({
        type: z.literal('tool_start'),
        id: z.string(),
        name: z.string(),
        label: z.string(),
        args: z.record(z.string(), z.unknown()),
    }),
    z.object({
        type: z.literal('tool_end'),
        id: z.string(),
        name: z.string(),
        ok: z.boolean(),
        /** The first lines of what the tool returned, for the expandable step. */
        preview: z.string(),
    }),
    z.object({
        type: z.literal('todos'),
        items: z.array(z.object({ content: z.string(), status: z.enum(['pending', 'in_progress', 'completed']) })),
    }),
    z.object({ type: z.literal('done'), response: chatMessageResponse }),
    z.object({ type: z.literal('error'), message: z.string() }),
]);
export const chatConfigResponse = z.object({
    enabled: z.boolean(),
    mode: z.enum(['stub', 'live']),
    retentionNotice: z.string(),
    /** Shown in the panel when mode is 'stub'. */
    stubNotice: z.string(),
});
// ------------------------------------------------------------------ uploads ---
export const fileUploadResponse = z.object({
    fileKey: z.string(),
    fileName: z.string(),
    fileSize: z.number().int(),
    mime: z.string(),
    downloadUrl: z.string(),
});
export const healthResponse = z.object({
    status: z.enum(['ok', 'degraded']),
    version: z.string(),
    checks: z.record(z.string(), z.object({ ok: z.boolean(), detail: z.string().optional() })),
});
//# sourceMappingURL=platform.js.map